import { action, computed, observable } from 'mobx'
import { orderBy } from 'lodash-es'
import type { NamedItem } from '@/features/legends/editor/legend-part-dependencies'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ObjectStorage } from '@/infra/object-storage'
import { StoredValue } from './StoredValue'

const SKILLS_KEY = 'previewer.selectedSkills'

/**
 * The skills of the pro the preview matches against. The selection is kept in
 * local storage per legend.
 */
export class SkillInputsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref accessor selectedSkillIds: string[] = []

  constructor(
    private readonly _getSkills: () => NamedItem[],
    getStorage: () => ObjectStorage,
    private readonly _getLegendId: () => Id | null,
  ) {
    this._lifecycle.manage(
      new StoredValue({
        getStorage,
        getKey: () => this._storageKey,
        getValue: () => this.selectedSkillIds,
        load: (stored) => {
          this.selectedSkillIds = Array.isArray(stored)
            ? stored.filter((id): id is string => typeof id === 'string')
            : []
        },
      }),
    )
  }

  @computed
  get availableSkills() {
    return orderBy(this._getSkills(), (s) => s.name)
  }

  /**
   * The workspace's skills, and whether each is selected.
   */
  @computed
  get skills() {
    return this.availableSkills.map((skill) => ({
      ...skill,
      isSelected: this.selectedSkillIds.includes(skill.id),
    }))
  }

  /**
   * The selected skills the workspace still has, as the engine takes them.
   */
  @computed
  get selectedSkills() {
    return this.availableSkills.filter((s) =>
      this.selectedSkillIds.includes(s.id),
    )
  }

  @computed
  get hasAllSkillsSelected() {
    return this.availableSkills.length === this.selectedSkills.length
  }

  @computed
  get hasSelectedSkills() {
    return this.selectedSkillIds.length > 0
  }

  @computed
  private get _storageKey() {
    const legendId = this._getLegendId()
    return legendId ? `${SKILLS_KEY}.${legendId}` : null
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Selects or deselects the skill.
   *
   * @param skillId
   */
  @action.bound
  toggleSkill(skillId: string) {
    this.selectedSkillIds = this.selectedSkillIds.includes(skillId)
      ? this.selectedSkillIds.filter((id) => id !== skillId)
      : [...this.selectedSkillIds, skillId]
  }

  /**
   * Selects every skill.
   */
  @action.bound
  selectAll() {
    this.selectedSkillIds = this.availableSkills.map((s) => s.id)
  }

  /**
   * Deselects every skill.
   */
  @action.bound
  clear() {
    this.selectedSkillIds = []
  }
}
